import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyDropoff } from "./key-dropoff/KeyDropoff";

import { useKeyValues } from "../../../components/SelectLanguage";
import Loading from "../../../components/Loading";
import { DomainContext } from "../../../domain-repository/DomainRepository";
import { KeylessWarningModal } from "../components/KeylessWarningModal";
import { getLanguages, getLocalizations } from "../../../services/localizations";
import { mapLocalizations, transformLocalsArrayToObject } from "../../select-language/SelectLanguagePage";
import { saveLocalStorageTranslations } from "../../../i18n";

import "./keylessReturnPage.css";

import {
  KeylessReturnLayout,
   RETURN_STEPS,
} from "./components/KeylessReturnLayout";
import { KeylessReturnFooter } from "./components/KeylessReturnFooter";
import { ReadQrCode } from "./read-qr-code/ReadQrCode";
import { ScanQrCode } from "./read-qr-code/ScanQrCode";
import { FailQrCodeContent } from "./read-qr-code/FailQrCodeContent";
import { RentalInFinalStage } from "./rental-in-final-stage/RentalInFinalStage";
import { WarningContent } from "./rental-in-final-stage/WarningContent";
import { FinishedRentalContent } from "./rental-in-final-stage/FinishedRentalContent";
import { VehicleDamageReportStep1 } from "./damage-report/VehicleDamageReportStep1";
import { VehicleDamageReportStep2 } from "./damage-report/VehicleDamageReportStep2";
import { VehicleDamageReportStep3 } from "./damage-report/VehicleDamageReportStep3";
import { VehicleDamageReportStep4 } from "./damage-report/VehicleDamageReportStep4";
import FuelLevelAndKms from "./fuel-level-and-kms/FuelLevelAndKms";
import ValidateFuelLevelAndKms from "./validate-fuel-level-and-kms/ValidateFuelLevelAndKms";
import Start from "./start/Start";
import Checklist from "./checklist/Checklist";
import { VehicleLocation } from "./vehicle-location/VehicleLocation";
import ParkDirections from "./park-directions/ParkDirections";
import { useKeylessReturn, STEPS, FuelType } from "./useKeylessReturn";

const STEP_TO_BREADCRUMB = {
  [STEPS.START]: RETURN_STEPS.START,
  [STEPS.VEHICLE_LOCATION]: RETURN_STEPS.START,
  [STEPS.PARK_DIRECTIONS]: RETURN_STEPS.START,
  [STEPS.FUEL_LEVEL]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.VALIDATE_FUEL_LEVEL_AND_KMS]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.CHECKLIST]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.VEHICLE_DAMAGE_REPORT_STEP_1]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.VEHICLE_DAMAGE_REPORT_STEP_2]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.VEHICLE_DAMAGE_REPORT_STEP_3]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.VEHICLE_DAMAGE_REPORT_STEP_4]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.KEY_DROP_OFF]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.READ_QR_CODE]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.SCAN_QR_CODE]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.FAIL_QR_CODE]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.RENTAL_FINAL_STAGE]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.WARNING_RENTAL]: RETURN_STEPS.IN_PROGRESS,
  [STEPS.FINISHED_RENTAL]: RETURN_STEPS.FINISHED,
};

export function KeylessReturnPage() {
  const { t, i18n } = useTranslation();
  const { localizationAssets, setLanguage, setLocalizationAssets, setLocalizations } = useContext(DomainContext);
  const { isLoading: isLoadingTranslations } = useKeyValues("pt");
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState("pt");
  const [switchingLang, setSwitchingLang] = useState(false);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    getLanguages().then((res) => setLanguages(res)).catch(() => {});
  }, []);

  const switchLanguage = useCallback((key) => {
    if (key === currentLang || switchingLang) return;
    setSwitchingLang(true);
    getLocalizations({ lang: key, system: "KIOSK" })
      .then((res) => {
        const mapped = mapLocalizations(key, key, res);
        setLocalizations(mapped);
        const mediaAssets = transformLocalsArrayToObject(mapped.filter((r) => r.type === "MEDIA"));
        setLocalizationAssets(mediaAssets);
        const texts = mapped.filter((r) => r.type === "TEXT");
        const obj = transformLocalsArrayToObject(texts);
        setLanguage(key);
        saveLocalStorageTranslations(key, obj);
        i18n.addResourceBundle(key, "translation", obj, true, true);
        i18n.changeLanguage(key);
        setCurrentLang(key);
      })
      .finally(() => setSwitchingLang(false));
  }, [currentLang, switchingLang, i18n, setLanguage, setLocalizationAssets, setLocalizations]);

  const {
    loading,
    keylessSession,
    currentStep,
    setCurrentStep,
    damageData,
    selectedPart,
    currentPhotos,
    currentNotes,
    setCurrentNotes,
    fuelLevel,
    setFuelLevel,
    kms,
    setKms,
    hasKmsRegistered,
    divergences,
    checklistChecks,
    setChecklistChecks,
    isChecklistComplete,
    step4Ref,
    submitting,
    warningModal,
    setWarningModal,
    manualPlate,
    setManualPlate,
    validatingVehicle,
    vehicleInvalid,
    handleAddDamage,
    handleRegisterDamage,
    handleCapturePhoto,
    handleRemovePhoto,
    handleSubmitDamageReport,
    handleValidateVehicle,
    handleValidateFuelLevelAndKms,
    handleStartKeylessReturn,
    handleWarningStep,
    handleParkDirections,
    selfcheckinPhotos,
    handleCaptureSelfcheckinPhoto,
    handleRemoveSelfcheckinPhoto,
    handleSubmitSelfcheckin,
  } = useKeylessReturn();

  if (isLoadingTranslations) {
    return <Loading active={true} />;
  }

  if (loading) {
    return <Loading active={true} />;
  }

  const isElectric = keylessSession?.fuelType === "EL";
  const vehicleFuelType = isElectric ? FuelType.ELECTRIC : FuelType.COMBUSTION;

  function getStepContent() {
    switch (currentStep) {
      case STEPS.START:
        return {
          content: (
            <Start
              contractNumber={
                <>
                  {t("keylessReturnContractNumber")} <strong>{keylessSession.raNumber}</strong>
                </>
              }
              t={t}
              localizationAssets={localizationAssets}
              onStart={handleStartKeylessReturn}
              helpModalOpen={helpModalOpen}
              setHelpModalOpen={setHelpModalOpen}
              currentLang={currentLang}
              onLanguageChange={switchLanguage}
              languages={languages}
            />
          ),
        };

      case STEPS.VEHICLE_LOCATION:
        return {
          title: "",
          content: <VehicleLocation username={keylessSession.driverName} t={t} />,
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnYes")}
              primaryOnClick={() => setCurrentStep(STEPS.FUEL_LEVEL)}
              secondaryText={t("keylessReturnNeedHelp")}
              secondaryOnClick={() => setCurrentStep(STEPS.PARK_DIRECTIONS)}
            />
          ),
        };

      case STEPS.PARK_DIRECTIONS:
        return {
          title: "",
          content: <ParkDirections t={t} />,
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnGoogleMaps")}
              primaryOnClick={handleParkDirections}
              secondaryText={t("keylessReturnBack")}
              secondaryOnClick={() => setCurrentStep(STEPS.VEHICLE_LOCATION)}
            />
          ),
        };

      case STEPS.FUEL_LEVEL:
        return {
          title: "",
          content: (
            <FuelLevelAndKms
              fuelType={vehicleFuelType}
              fuelLevel={fuelLevel}
              setFuelLevel={setFuelLevel}
              kms={kms}
              setKms={setKms}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnYes")}
              primaryOnClick={handleValidateFuelLevelAndKms}
              primaryDisabled={!hasKmsRegistered}
              secondaryText={t("keylessReturnNo")}
              secondaryOnClick={() => setCurrentStep(STEPS.VEHICLE_LOCATION)}
            />
          ),
        };

      case STEPS.VALIDATE_FUEL_LEVEL_AND_KMS:
        return {
          title: isElectric ? t("keylessReturnChargeLevel") : t("keylessReturnFuelLevel"),
          content: (
            <ValidateFuelLevelAndKms
              fuelType={vehicleFuelType}
              fuelLevel={fuelLevel}
              setFuelLevel={setFuelLevel}
              kms={kms}
              setKms={setKms}
              fuelDivergence={divergences.fuel}
              kmsDivergence={divergences.kms}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={() => {
                setCurrentStep(STEPS.CHECKLIST);
              }}
              primaryDisabled={!hasKmsRegistered}
              showSecondary={false}
            />
          ),
        };

      case STEPS.CHECKLIST:
        return {
          title: t("keylessReturnChecklist"),
          content: (
            <Checklist
              checklistChecks={checklistChecks}
              setChecklistChecks={setChecklistChecks}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={() => {
                if (isChecklistComplete) {
                  setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_1);
                }
              }}
              primaryDisabled={!isChecklistComplete}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() => setCurrentStep(STEPS.FUEL_LEVEL)}
            />
          ),
        };

      case STEPS.VEHICLE_DAMAGE_REPORT_STEP_1:
        return {
          title: t("keylessReturnVehicleCheck"),
          content: <VehicleDamageReportStep1 t={t} />,
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={() =>
                setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_2)
              }
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() => setCurrentStep(STEPS.CHECKLIST)}
            />
          ),
        };

      case STEPS.VEHICLE_DAMAGE_REPORT_STEP_2:
        return {
          title: t("keylessReturnVehicleCheck"),
          content: submitting ? (
            <Loading active={true} />
          ) : (
            <VehicleDamageReportStep2
              damageData={damageData}
              initialExpandedPart={selectedPart}
              onAdd={handleAddDamage}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={handleSubmitDamageReport}
              primaryDisabled={submitting}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() =>
                setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_1)
              }
            />
          ),
        };

      case STEPS.VEHICLE_DAMAGE_REPORT_STEP_3:
        return {
          title: t("keylessReturnVehicleCheck"),
          content: (
            <VehicleDamageReportStep3
              photos={currentPhotos}
              notes={currentNotes}
              onNotesChange={setCurrentNotes}
              onInsertPhoto={() =>
                setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_4)
              }
              onRemovePhoto={handleRemovePhoto}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnRegisterDamage")}
              primaryOnClick={handleRegisterDamage}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() =>
                setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_2)
              }
            />
          ),
        };

      case STEPS.VEHICLE_DAMAGE_REPORT_STEP_4:
        return {
          title: t("keylessReturnVehicleCheck"),
          content: (
            <VehicleDamageReportStep4
              ref={step4Ref}
              onCapture={handleCapturePhoto}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnCapturePhoto")}
              primaryOnClick={() => step4Ref.current?.capture()}
              secondaryText={t("keylessReturnCancel")}
              secondaryOnClick={() =>
                setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_3)
              }
            />
          ),
        };

      case STEPS.SELFCHECKIN_PHOTO:
        return {
          title: t("keylessReturnVehicleCheck"),
          content: (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: "100%", gap: "24px" }}>
              <p style={{ fontSize: "14px", color: "#333", margin: 0, lineHeight: "1.4" }}>
                {t("keylessReturnSelfcheckinMsg")}
              </p>
              <button
                onClick={() => setCurrentStep(STEPS.SELFCHECKIN_CAMERA)}
                style={{
                  backgroundColor: "#8CC63F", color: "#FFFFFF", border: "none", borderRadius: "8px",
                  padding: "14px", fontSize: "16px", fontWeight: "bold", cursor: "pointer", width: "100%",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                {t("keylessReturnInsertPhoto")}
              </button>
              {selfcheckinPhotos.length > 0 && (
                <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  {selfcheckinPhotos.map((photo) => (
                    <div key={photo.id} style={{ position: "relative", borderRadius: "8px", overflow: "hidden", border: "1px solid #E0E0E0", aspectRatio: "16/9" }}>
                      <img src={photo.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      <button
                        onClick={() => handleRemoveSelfcheckinPhoto(photo.id)}
                        style={{
                          position: "absolute", top: "4px", right: "4px", width: "22px", height: "22px",
                          borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.6)", color: "white",
                          border: "none", fontSize: "13px", fontWeight: "bold", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0,
                        }}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={selfcheckinPhotos.length > 0 ? t("keylessReturnNext") : t("keylessReturnTakePhoto")}
              primaryOnClick={selfcheckinPhotos.length > 0 ? handleSubmitSelfcheckin : () => setCurrentStep(STEPS.SELFCHECKIN_CAMERA)}
              secondaryText={selfcheckinPhotos.length > 0 ? t("keylessReturnTakePhoto") : t("keylessReturnPrevious")}
              secondaryOnClick={selfcheckinPhotos.length > 0 ? () => setCurrentStep(STEPS.SELFCHECKIN_CAMERA) : () => setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_2)}
            />
          ),
        };

      case STEPS.SELFCHECKIN_CAMERA:
        return {
          title: t("keylessReturnVehicleCheck"),
          content: (
            <VehicleDamageReportStep4
              ref={step4Ref}
              onCapture={handleCaptureSelfcheckinPhoto}
              t={(key) => key === "keylessReturnDamageSubmit" ? t("keylessReturnSelfcheckinMsg") : t(key)}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnCapturePhoto")}
              primaryOnClick={() => step4Ref.current?.capture()}
              secondaryText={t("keylessReturnCancel")}
              secondaryOnClick={() => setCurrentStep(STEPS.SELFCHECKIN_PHOTO)}
            />
          ),
        };

      case STEPS.KEY_DROP_OFF:
        return {
          title: t("keylessReturnKeyDropoff"),
          content: <KeyDropoff t={t} />,
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={() => setCurrentStep(STEPS.READ_QR_CODE)}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() =>
                setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_2)
              }
            />
          ),
        };

      case STEPS.READ_QR_CODE:
        return {
          title: t("keylessReturnReadQrCode"),
          content: <ReadQrCode t={t} />,
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnScanQrButton")}
              primaryOnClick={() => setCurrentStep(STEPS.SCAN_QR_CODE)}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() => setCurrentStep(STEPS.KEY_DROP_OFF)}
            />
          ),
        };

      case STEPS.SCAN_QR_CODE:
        return {
          title: t("keylessReturnScanQrCode"),
          content: (
            <ScanQrCode
              onSuccess={(qrText) => handleValidateVehicle(qrText)}
              onError={() => setCurrentStep(STEPS.FAIL_QR_CODE)}
              t={t}
            />
          ),
          footer: null,
        };

      case STEPS.FAIL_QR_CODE:
        return {
          title: t("keylessReturnFailQrCode"),
          content: (
            <FailQrCodeContent
              manualPlate={manualPlate}
              onManualPlateChange={setManualPlate}
              t={t}
            />
          ),
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={() =>
                handleValidateVehicle(`null|${manualPlate.trim()}`)
              }
              primaryDisabled={!manualPlate.trim() || validatingVehicle}
              primaryLoading={validatingVehicle}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() => setCurrentStep(STEPS.READ_QR_CODE)}
            />
          ),
        };

      case STEPS.RENTAL_FINAL_STAGE:
        return {
          title: t("keylessReturnFinishing"),
          content: <RentalInFinalStage t={t} />,
          footer: null,
        };

      case STEPS.WARNING_RENTAL:
        return {
          title: t("keylessReturnWarning"),
          content: <WarningContent t={t} />,
          footer: (
            <KeylessReturnFooter
              primaryText={t("keylessReturnNext")}
              primaryOnClick={handleWarningStep}
              secondaryText={t("keylessReturnPrevious")}
              secondaryOnClick={() => setCurrentStep(STEPS.FAIL_QR_CODE)}
            />
          ),
        };

      case STEPS.FINISHED_RENTAL:
        return {
          title: t("keylessReturnFinished"),
          content: <FinishedRentalContent t={t} />,
          footer: (
            <></>
            // Disabled for now
            // <KeylessReturnFooter
            //   primaryText="Finalizar Devolução"
            //   primaryOnClick={() =>
            //     setCurrentStep(STEPS.START)
            //   }
            //   showSecondary={false}
            // />
          ),
        };

      default:
        return { title: t("keylessReturnError"), content: null, footer: null };
    }
  }

  const { title, content, footer } = getStepContent();

  return currentStep === STEPS.START ? (
    content
  ) : (
    <KeylessReturnLayout
      step={STEP_TO_BREADCRUMB[currentStep]}
      localizationAssets={localizationAssets}
      helpModalOpen={helpModalOpen}
      setHelpModalOpen={setHelpModalOpen}
      t={t}
      contractNumber={
        <>
          {t("keylessReturnContractNumber")} <strong>{keylessSession.raNumber}</strong>
        </>
      }
      footer={footer}
    >
      <div className="container-return">
        <h2 className="key-return-title">{title}</h2>
        {content}
      </div>

      <KeylessWarningModal
        isOpen={warningModal.isOpen}
        title={t(warningModal.title)}
        message={t(warningModal.message)}
        onClose={() =>
          setWarningModal({ isOpen: false, title: "", message: "" })
        }
      />

      <KeylessWarningModal
        isOpen={vehicleInvalid}
        title={t("keylessReturnInvalidVehicle")}
        message={t("keylessReturnInvalidVehicleMsg")}
        buttonText={t("keylessReturnExit")}
        onClose={() => {
          window.location.href = "/";
        }}
      />
    </KeylessReturnLayout>
  );
}
